import { appointmentsAPI, AppointmentCreateRequest } from '../../api/appointments';
import { createClinicAppointment, CreateClinicAppointmentData } from '../../api/clinic';
import { WriteHandler } from './types';
import {
  pullSoloScheduleIntoCache,
  pullClinicScheduleIntoCache,
  getCachedSoloSchedule,
  getCachedClinicSchedule,
} from '../scheduleSync';
import { findNextAvailableSlot, SlotExistingAppointment } from '../nextAvailableSlot';

/**
 * The one real conflict case left under the create-only/auto-slot design
 * (see the Phase 2 plan): an offline-picked slot got taken by the time of
 * sync -- another offline device picked it too, or someone booked it
 * online in the meantime. The backend's overlap validation
 * (scheduling.Appointment.clean / ClinicAppointmentCreateSerializer.
 * validate) already rejects this; on rejection we re-pull fresh schedule
 * data, recompute the next available slot, and retry once, then tell the
 * user their booking's time shifted rather than silently double-booking
 * or failing.
 */

function isConflictError(err: unknown): boolean {
  const message = (err as any)?.response?.data;
  const text = typeof message === 'string' ? message : JSON.stringify(message ?? '');
  return (
    text.includes('no está disponible') ||
    text.includes('Ya existe una cita') ||
    text.includes('bloqueada')
  );
}

export const createAppointmentHandler: WriteHandler = async (row) => {
  const payload = JSON.parse(row.payload) as AppointmentCreateRequest;

  try {
    await appointmentsAPI.create(payload);
    return;
  } catch (err) {
    if (!isConflictError(err)) throw err;
  }

  await pullSoloScheduleIntoCache(payload.professional);
  const { workingDays, blockedDates, appointments } = await getCachedSoloSchedule(payload.professional);
  const blockingAppointments: SlotExistingAppointment[] = appointments.filter(
    (a: any) => a.status !== 'cancelled'
  );

  const nextSlot = findNextAvailableSlot({ workingDays, blockedDates, existingAppointments: blockingAppointments });
  if (!nextSlot) {
    throw new Error('No hay horarios disponibles para reasignar esta cita.');
  }

  const originalDate = payload.date;
  const originalTime = payload.start_time;
  await appointmentsAPI.create({ ...payload, date: nextSlot.date, start_time: nextSlot.start_time, end_time: nextSlot.end_time });

  window.alert(
    `Una cita se reagendó automáticamente porque el horario original (${originalDate} ${originalTime}) ya no estaba disponible.\n\nNuevo horario: ${nextSlot.date} ${nextSlot.start_time}.`
  );
};

export const createClinicAppointmentHandler: WriteHandler = async (row) => {
  const payload = JSON.parse(row.payload) as CreateClinicAppointmentData;

  try {
    await createClinicAppointment(payload);
    return;
  } catch (err) {
    if (!isConflictError(err)) throw err;
  }

  await pullClinicScheduleIntoCache(payload.clinic, payload.professional);
  const { workingDays, blockedDates, appointments } = await getCachedClinicSchedule(
    payload.clinic,
    payload.professional
  );
  const blockingAppointments: SlotExistingAppointment[] = appointments.filter((a: any) =>
    ['pending', 'confirmed'].includes(a.status)
  );

  const nextSlot = findNextAvailableSlot({ workingDays, blockedDates, existingAppointments: blockingAppointments });
  if (!nextSlot) {
    throw new Error('No hay horarios disponibles para reasignar esta cita.');
  }

  const originalDate = payload.date;
  const originalTime = payload.start_time;
  await createClinicAppointment({
    ...payload,
    date: nextSlot.date,
    start_time: nextSlot.start_time,
    end_time: nextSlot.end_time,
  });

  window.alert(
    `Una cita se reagendó automáticamente porque el horario original (${originalDate} ${originalTime}) ya no estaba disponible.\n\nNuevo horario: ${nextSlot.date} ${nextSlot.start_time}.`
  );
};
